import { useState, useRef, useEffect } from 'react';
import { X, MapPin, ClipboardList, Plus, Trash2, Navigation, CheckCircle } from 'lucide-react';
import { useStore, type TimeSlot } from '../store/useStore';
import { PointIcon } from './icons/PointIcon';
import mapboxgl from 'mapbox-gl';

const ICON_OPTIONS = ['MapPin','Music','Utensils','Shirt','GlassWater','Ticket','Shield','Car','Tent','FirstAid','Wrench','Camera','Mic','Palette','Zap'];
const MAPBOX_TOKEN = (import.meta as any).env.VITE_MAPBOX_ACCESS_TOKEN || '';
mapboxgl.accessToken = MAPBOX_TOKEN;

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

function MiniMap({ lat, lng, onLocationSelected }: { lat: number | null; lng: number | null; onLocationSelected: (lat: number, lng: number) => void }) {
  const mapContainer = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const markerRef = useRef<mapboxgl.Marker | null>(null);

  const center: [number, number] = lat && lng ? [lng, lat] : [10.0, 36.0]; // Default to Tunisia

  useEffect(() => {
    if (!mapContainer.current || mapRef.current) return;

    const map = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/dark-v11',
      center,
      zoom: lat && lng ? 16 : 5,
      attributionControl: false,
    });

    map.addControl(new mapboxgl.NavigationControl(), 'top-right');

    // Click to place marker
    map.on('click', (e) => {
      onLocationSelected(e.lngLat.lat, e.lngLat.lng);
    });

    mapRef.current = map;

    // Add marker if we have coords
    if (lat && lng) {
      const marker = new mapboxgl.Marker({ color: '#6366F1', draggable: true })
        .setLngLat([lng, lat])
        .addTo(map);
      marker.on('dragend', () => {
        const pos = marker.getLngLat();
        onLocationSelected(pos.lat, pos.lng);
      });
      markerRef.current = marker;
    }

    return () => {
      map.remove();
      mapRef.current = null;
      markerRef.current = null;
    };
  }, []);

  // Update marker when lat/lng changes externally (search result)
  useEffect(() => {
    if (!mapRef.current) return;

    if (lat && lng) {
      mapRef.current.flyTo({ center: [lng, lat], zoom: 16, duration: 800 });

      if (markerRef.current) {
        markerRef.current.setLngLat([lng, lat]);
      } else {
        const marker = new mapboxgl.Marker({ color: '#6366F1', draggable: true })
          .setLngLat([lng, lat])
          .addTo(mapRef.current);
        marker.on('dragend', () => {
          const pos = marker.getLngLat();
          onLocationSelected(pos.lat, pos.lng);
        });
        markerRef.current = marker;
      }
    }
  }, [lat, lng]);

  return (
    <div className="relative">
      <div ref={mapContainer} className="w-full h-[220px] rounded-xl border border-[#2D3139]" />
      <div className="absolute top-2 left-2 bg-black/70 text-white text-[9px] px-1.5 py-0.5 rounded pointer-events-none">
        Cliquez sur la carte pour placer le point
      </div>
      {lat && lng && (
        <div className="absolute bottom-2 left-2 bg-black/70 text-white text-[9px] px-1.5 py-0.5 rounded">
          {lat.toFixed(5)}, {lng.toFixed(5)}
        </div>
      )}
    </div>
  );
}

export function AddPointTaskModal({ isOpen, onClose }: Props) {
  const { currentUser, categories, addPoint, addTask, addCategory } = useStore();
  const isAdmin = currentUser.role === 'admin';

  const [mode, setMode] = useState<'point' | 'task'>('point');
  const [name, setName] = useState('');
  const [categoryId, setCategoryId] = useState(currentUser.categoryId || '');
  const [iconName, setIconName] = useState('MapPin');
  const [requiredTeam, setRequiredTeam] = useState(1);
  const [description, setDescription] = useState('');
  const [slots, setSlots] = useState<TimeSlot[]>([{ start: '', end: '', requiredTeam: 1 }]);
  // Point-only fields
  const [lat, setLat] = useState<number | null>(null);
  const [lng, setLng] = useState<number | null>(null);
  const [addressQuery, setAddressQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [showMap, setShowMap] = useState(false);
  const [isStage, setIsStage] = useState(false);
  const [isRestricted, setIsRestricted] = useState(false);

  // All hooks must be before any early return
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autocompleteSvc = useRef<any>(null);
  const geocoderSvc = useRef<any>(null);
  const placesSessionToken = useRef<any>(null);
  const googleLoaded = useRef(false);
  const [showCatForm, setShowCatForm] = useState(false);
  const [newCatName, setNewCatName] = useState('');
  const [newCatArtist, setNewCatArtist] = useState(false);

  // Dynamically load Google Maps JS if not already loaded (Live Map may not have been opened yet)
  useEffect(() => {
    if (!isOpen || googleLoaded.current) return;
    const g = (window as any).google;
    if (g?.maps?.places) { googleLoaded.current = true; return; }
    const key = (import.meta as any).env.VITE_GOOGLE_MAPS_PLATFORM_KEY || 'AIzaSyCorlHfn18x02lhn-_Fh6oY_9_qAI_E2A4';
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${key}&libraries=places&language=fr`;
    script.async = true;
    script.onload = () => { googleLoaded.current = true; };
    document.head.appendChild(script);
  }, [isOpen]);

  if (!isOpen) return null;

  const visibleCategories = categories.filter(c => !c.isGeneral);
  const selectedCategory = categoryId ? categories.find(c => c.id === categoryId) : null;
  const isArtistCat = selectedCategory?.isArtistCategory === true;

  const handleLocationSelected = (newLat: number, newLng: number) => {
    setLat(newLat);
    setLng(newLng);
  };

  const getGoogleLibs = () => {
    const g = (window as any).google;
    if (!g?.maps?.places) return false;
    if (!autocompleteSvc.current) autocompleteSvc.current = new g.maps.places.AutocompleteService();
    if (!geocoderSvc.current) geocoderSvc.current = new g.maps.Geocoder();
    if (!placesSessionToken.current) placesSessionToken.current = new g.maps.places.AutocompleteSessionToken();
    return true;
  };

  const searchAddress = (query: string) => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    if (!query.trim()) { setSuggestions([]); return; }
    if (!getGoogleLibs()) { setSuggestions([]); return; }

    searchTimeout.current = setTimeout(() => {
      setSearching(true);
      autocompleteSvc.current.getPlacePredictions(
        { input: query, language: 'fr', sessionToken: placesSessionToken.current },
        (predictions: any[], status: string) => {
          if (status === 'OK' && predictions) {
            setSuggestions(predictions.map((p: any) => ({ place_id: p.place_id, name: p.description })));
          } else {
            setSuggestions([]);
          }
          setSearching(false);
        }
      );
    }, 200);
  };

  const selectLocation = (suggestion: any) => {
    setAddressQuery(suggestion.name);
    setSuggestions([]);
    setSearching(true);
    setShowMap(true);
    if (!getGoogleLibs()) { setSearching(false); return; }
    geocoderSvc.current.geocode({ placeId: suggestion.place_id }, (results: any[], status: string) => {
      if (status === 'OK' && results?.[0]?.geometry?.location) {
        setLat(results[0].geometry.location.lat());
        setLng(results[0].geometry.location.lng());
      }
      setSearching(false);
    });
  };

  const openMapWithoutSearch = () => {
    setShowMap(true);
  };

  const addSlot = () => setSlots([...slots, { start: '', end: '', requiredTeam: 1 }]);
  const removeSlot = (i: number) => setSlots(slots.filter((_, idx) => idx !== i));
  const updateSlot = (i: number, f: Partial<TimeSlot>) => {
    setSlots(slots.map((s, idx) => idx === i ? { ...s, ...f } : s));
  };

  const handleSubmit = () => {
    if (!name.trim()) return;
    if (isAdmin && !categoryId) { alert('Veuillez sélectionner une catégorie.'); return; }
    const hours = slots.filter(s => s.start && s.end);
    if (hours.length === 0) return;

    if (mode === 'point') {
      addPoint({
        name: name.trim(),
        categoryId: categoryId || undefined,
        iconName,
        requiredTeam,
        descriptionMarkdown: description || undefined,
        openingHours: hours,
        location: lat && lng ? { lat, lng } : undefined,
        isMapFocus: true,
        isStage: isArtistCat ? isStage : false,
        isRestricted,
      });
    } else {
      addTask({
        name: name.trim(),
        categoryId: categoryId || undefined,
        iconName,
        requiredTeam,
        descriptionMarkdown: description || undefined,
        openingHours: hours,
        projectId: undefined,
      });
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[150] flex items-start justify-center pt-10 px-4 overflow-y-auto">
      <div className="bg-[#14161B] border border-[#2D3139] rounded-2xl w-full max-w-xl shadow-2xl">
        {/* Header */}
        <div className="p-5 border-b border-[#2D3139] flex items-center justify-between">
          <h2 className="font-bold text-white text-lg flex items-center gap-2">
            {mode === 'point' ? <MapPin className="w-5 h-5 text-indigo-400" /> : <ClipboardList className="w-5 h-5 text-emerald-400" />}
            {mode === 'point' ? 'Nouveau Point' : 'Nouvelle Tâche'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-[#2D3139] rounded-xl text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto custom-scrollbar">
          {/* Mode toggle */}
          <div className="flex gap-2 p-1 bg-[#0A0B0E] rounded-xl">
            <button onClick={() => setMode('point')} className={`flex-1 py-2.5 rounded-lg text-xs font-bold flex items-center justify-center gap-2 transition-all ${mode === 'point' ? 'bg-indigo-600 text-white shadow-lg' : 'text-slate-400 hover:text-white'}`}>
              <MapPin className="w-3.5 h-3.5" /> Point
            </button>
            <button onClick={() => setMode('task')} className={`flex-1 py-2.5 rounded-lg text-xs font-bold flex items-center justify-center gap-2 transition-all ${mode === 'task' ? 'bg-emerald-600 text-white shadow-lg' : 'text-slate-400 hover:text-white'}`}>
              <ClipboardList className="w-3.5 h-3.5" /> Tâche
            </button>
          </div>

          {/* Name */}
          <div>
            <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5 block">Nom</label>
            <input value={name} onChange={e => setName(e.target.value)} placeholder={mode === 'point' ? 'Ex: Scène Principale' : 'Ex: Briefing Équipe'} className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-xl px-3 py-2.5 text-white text-sm placeholder-slate-600 focus:border-indigo-500 focus:outline-none" />
          </div>

          {/* Category (admin only — required) */}
          {isAdmin && (
            <div>
              <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5 block">
                Catégorie <span className="text-red-400">*</span>
              </label>
              <div className="flex gap-2">
                <select value={categoryId} onChange={e => { setCategoryId(e.target.value); setIsStage(false); }} className="flex-1 bg-[#0A0B0E] border border-[#2D3139] rounded-xl px-3 py-2.5 text-white text-sm focus:border-indigo-500 focus:outline-none">
                  <option value="">-- Sélectionner --</option>
                  {visibleCategories.map(c => <option key={c.id} value={c.id}>{c.name}{c.isArtistCategory ? ' 🎨' : ''}</option>)}
                </select>
                <button
                  onClick={() => setShowCatForm(!showCatForm)}
                  className="px-3 py-2 bg-[#1A1D23] border border-[#2D3139] hover:border-indigo-500/50 text-indigo-400 hover:text-indigo-300 rounded-xl text-xs font-bold flex items-center gap-1 flex-shrink-0"
                  title="Créer une catégorie"
                >
                  <Plus className="w-3 h-3" />
                </button>
              </div>
              {showCatForm && (
                <div className="mt-2 p-3 bg-[#0A0B0E] border border-[#2D3139] rounded-xl space-y-2">
                  <input
                    value={newCatName}
                    onChange={e => setNewCatName(e.target.value)}
                    placeholder="Nom de la catégorie"
                    className="w-full bg-[#14161B] border border-[#2D3139] rounded-lg px-3 py-2 text-white text-xs placeholder-slate-600 focus:border-indigo-500 focus:outline-none"
                  />
                  <div className="flex items-center justify-between">
                    <label className="flex items-center gap-1.5 text-[10px] text-slate-400 cursor-pointer">
                      <input type="checkbox" checked={newCatArtist} onChange={e => setNewCatArtist(e.target.checked)} className="rounded accent-amber-500" /> Catégorie artiste 🎨
                    </label>
                    <div className="flex gap-2">
                      <button onClick={() => setShowCatForm(false)} className="text-[10px] text-slate-500 hover:text-white px-2 py-1">Annuler</button>
                      <button
                        onClick={() => {
                          if (!newCatName.trim()) return;
                          addCategory({ name: newCatName.trim(), isArtistCategory: newCatArtist, isGeneral: false, isPublic: false });
                          setNewCatName(''); setNewCatArtist(false); setShowCatForm(false);
                        }}
                        className="text-[10px] font-bold bg-indigo-600 hover:bg-indigo-500 text-white px-3 py-1 rounded-lg"
                      >
                        Créer
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Icon + Required team + Options */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5 block">Icône</label>
              <div className="flex flex-wrap gap-1.5 bg-[#0A0B0E] border border-[#2D3139] rounded-xl p-2">
                {ICON_OPTIONS.map(icon => (
                  <button key={icon} onClick={() => setIconName(icon)} className={`p-1.5 rounded-lg transition-all ${iconName === icon ? 'bg-indigo-500/20 border border-indigo-500/40 text-indigo-400' : 'hover:bg-[#2D3139] text-slate-500'}`}>
                    <PointIcon name={icon} size={16} />
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5 block">Team requis</label>
              <input type="number" min={1} value={requiredTeam} onChange={e => setRequiredTeam(parseInt(e.target.value) || 1)} className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-xl px-3 py-2.5 text-white text-sm focus:border-indigo-500 focus:outline-none" />
              {isAdmin && (
                <div className="flex flex-col gap-1.5 mt-2">
                  {isArtistCat && (
                    <button
                      type="button"
                      onClick={() => setIsStage(!isStage)}
                      className={`w-full text-left px-3 py-2 rounded-lg text-xs font-bold border transition-all flex items-center gap-2 ${
                        isStage ? 'bg-amber-500/20 border-amber-500/40 text-amber-300' : 'bg-[#0A0B0E] border-[#2D3139] text-slate-500 hover:text-amber-400'
                      }`}
                    >
                      <span className="text-base">🎨</span>
                      <div>
                        <span className="block">Scène</span>
                        <span className="text-[8px] font-normal opacity-70">Réservé aux artistes uniquement</span>
                      </div>
                      {isStage && <CheckCircle className="w-3.5 h-3.5 ml-auto text-amber-400" />}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setIsRestricted(!isRestricted)}
                    className={`w-full text-left px-3 py-2 rounded-lg text-xs font-bold border transition-all flex items-center gap-2 ${
                      isRestricted ? 'bg-indigo-500/20 border-indigo-500/40 text-indigo-300' : 'bg-[#0A0B0E] border-[#2D3139] text-slate-500 hover:text-indigo-400'
                    }`}
                  >
                    <span className="text-base">🔒</span>
                    <div>
                      <span className="block">Restreint</span>
                      <span className="text-[8px] font-normal opacity-70">Teams avec spécialité uniquement</span>
                    </div>
                    {isRestricted && <CheckCircle className="w-3.5 h-3.5 ml-auto text-indigo-400" />}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Location search + Interactive mini-map (Point only) */}
          {mode === 'point' && (
            <div>
              <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5 block">
                Emplacement <span className="text-slate-600 font-normal">(optionnel)</span>
              </label>

              {/* Search bar */}
              <div className="flex gap-2 mb-2">
                <div className="relative flex-1">
                  <input
                    value={addressQuery}
                    onChange={e => { setAddressQuery(e.target.value); searchAddress(e.target.value); }}
                    placeholder="Rechercher un lieu (Google Maps)..."
                    className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-xl pl-3 pr-8 py-2.5 text-white text-sm placeholder-slate-600 focus:border-indigo-500 focus:outline-none"
                  />
                  {searching && <div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />}
                </div>
                <button
                  onClick={openMapWithoutSearch}
                  className="px-3 py-2 bg-[#1A1D23] border border-[#2D3139] hover:border-indigo-500/50 text-slate-400 hover:text-white rounded-xl text-xs font-bold flex items-center gap-1.5 flex-shrink-0"
                >
                  <Navigation className="w-3.5 h-3.5" /> Carte
                </button>
              </div>

              {/* Suggestions dropdown */}
              {suggestions.length > 0 && (
                <div className="mb-2 bg-[#0A0B0E] border border-[#2D3139] rounded-xl max-h-40 overflow-y-auto custom-scrollbar">
                  {suggestions.map((s, i) => (
                    <button key={i} onClick={() => selectLocation(s)} className="w-full text-left px-3 py-2 text-xs text-slate-300 hover:bg-[#1A1D23] border-b border-[#2D3139]/30 last:border-0">
                      <MapPin className="w-3 h-3 inline mr-1.5 text-slate-500" />{s.name}
                    </button>
                  ))}
                </div>
              )}

              {/* Interactive mini-map */}
              {showMap && (
                <div className="rounded-xl overflow-hidden">
                  <MiniMap lat={lat} lng={lng} onLocationSelected={handleLocationSelected} />
                </div>
              )}

              {/* Show button to open map if not already shown */}
              {!showMap && lat == null && lng == null && (
                <p className="text-[10px] text-slate-600 italic text-center py-3 border border-dashed border-[#2D3139] rounded-xl">
                  Recherchez un lieu ou cliquez sur "Carte" pour placer le point manuellement
                </p>
              )}
            </div>
          )}

          {/* Time slots */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Créneaux horaires</label>
              <button onClick={addSlot} className="text-[10px] font-bold text-indigo-400 hover:text-indigo-300 flex items-center gap-1"><Plus className="w-3 h-3" /> Ajouter</button>
            </div>
            <div className="space-y-2">
              {slots.map((s, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input type="datetime-local" value={s.start} onChange={e => updateSlot(i, { start: e.target.value })} className="flex-1 bg-[#0A0B0E] border border-[#2D3139] rounded-lg px-2.5 py-2 text-xs text-white focus:border-indigo-500 focus:outline-none" />
                  <span className="text-slate-500 text-xs">→</span>
                  <input type="datetime-local" value={s.end} onChange={e => updateSlot(i, { end: e.target.value })} className="flex-1 bg-[#0A0B0E] border border-[#2D3139] rounded-lg px-2.5 py-2 text-xs text-white focus:border-indigo-500 focus:outline-none" />
                  <input type="number" min={1} value={s.requiredTeam || 1} onChange={e => updateSlot(i, { requiredTeam: parseInt(e.target.value) || 1 })} className="w-12 bg-[#0A0B0E] border border-[#2D3139] rounded-lg px-2 py-2 text-xs text-white text-center focus:border-indigo-500 focus:outline-none" title="Team requis" />
                  {slots.length > 1 && <button onClick={() => removeSlot(i)} className="text-red-400 hover:text-red-300"><Trash2 className="w-3.5 h-3.5" /></button>}
                </div>
              ))}
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5 block">Description (optionnel)</label>
            <textarea value={description} onChange={e => setDescription(e.target.value)} rows={2} placeholder="Notes, instructions..." className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-xl px-3 py-2.5 text-white text-sm placeholder-slate-600 focus:border-indigo-500 focus:outline-none resize-none" />
          </div>
        </div>

        {/* Footer */}
        <div className="p-5 border-t border-[#2D3139] flex gap-3">
          <button onClick={onClose} className="flex-1 py-2.5 bg-[#1A1D23] hover:bg-[#2D3139] border border-[#2D3139] text-slate-400 font-bold text-sm rounded-xl transition-colors">Annuler</button>
          <button onClick={handleSubmit} disabled={!name.trim() || !slots.some(s => s.start && s.end)} className={`flex-1 py-2.5 font-bold text-sm rounded-xl transition-colors disabled:opacity-40 ${mode === 'point' ? 'bg-indigo-600 hover:bg-indigo-500 text-white' : 'bg-emerald-600 hover:bg-emerald-500 text-white'}`}>
            Créer {mode === 'point' ? 'le Point' : 'la Tâche'}
          </button>
        </div>
      </div>
    </div>
  );
}
